import axios from 'axios'

/**
 * Erro de negócio tipado do conversations-ms.
 *
 * `code` só vem em erro de negócio: 400/401/403/404 **não** trazem `code` e a
 * decisão nesses casos é pelo status. Mesmo contrato do app React Native e do
 * star-portal — três entendimentos diferentes do mesmo backend seria pior que
 * nenhum.
 */
export type ConversationErrorCode =
  | 'PROFESSIONAL_CONVERSATION_LIMIT'
  | 'PATIENT_CONVERSATION_LIMIT'
  | 'TWILIO_UNAVAILABLE'
  | 'DEPENDENCY_UNAVAILABLE'
  | 'TWILIO_AUTH'
  | 'CONVERSATION_CLOSED'

const KNOWN_CODES: string[] = [
  'PROFESSIONAL_CONVERSATION_LIMIT',
  'PATIENT_CONVERSATION_LIMIT',
  'TWILIO_UNAVAILABLE',
  'DEPENDENCY_UNAVAILABLE',
  'TWILIO_AUTH',
  'CONVERSATION_CLOSED',
]

/** Fallback quando o 503 vem sem `Retry-After` — mesmo default do backend. */
export const DEFAULT_RETRY_AFTER_SECONDS = 300

export class ConversationsApiError extends Error {
  readonly status: number

  readonly code: ConversationErrorCode | undefined

  readonly retryAfterSeconds: number | undefined

  /** Texto do servidor. Serve para log — nunca para a tela. */
  readonly serverMessage: string

  constructor(
    status: number,
    code: ConversationErrorCode | undefined,
    retryAfterSeconds: number | undefined,
    serverMessage: string
  ) {
    super(serverMessage)
    this.name = 'ConversationsApiError'
    this.status = status
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
    this.serverMessage = serverMessage
    // `extends Error` com target ES5 quebra o `instanceof` sem isto.
    Object.setPrototypeOf(this, ConversationsApiError.prototype)
  }

  /** Instabilidade de dependência: vale retentar com backoff. */
  get isTransient(): boolean {
    return (
      this.code === 'TWILIO_UNAVAILABLE' ||
      this.code === 'DEPENDENCY_UNAVAILABLE'
    )
  }

  /**
   * Falta capacidade. É a cota de 1000 conversas por identidade do Twilio, e
   * neste cliente ela satura primeiro: o profissional soma o volume de todos os
   * pacientes dele. Retentar foi a origem do incidente — aqui o cliente para.
   */
  /**
   * Conversa encerrada (409). Não é falta de capacidade nem erro transitório:
   * a resposta **não muda** sem um `reopen`, então retentar é rajada inútil.
   */
  get isClosed(): boolean {
    return this.code === 'CONVERSATION_CLOSED'
  }

  get isCapacity(): boolean {
    return (
      this.code === 'PROFESSIONAL_CONVERSATION_LIMIT' ||
      this.code === 'PATIENT_CONVERSATION_LIMIT'
    )
  }

  /** Bug de cliente ou credencial: retentar não resolve nunca. */
  get isPermanent(): boolean {
    return (
      this.code === 'TWILIO_AUTH' || [400, 401, 403].indexOf(this.status) >= 0
    )
  }
}

function parseCode(data: unknown): ConversationErrorCode | undefined {
  if (typeof data !== 'object' || data === null) return undefined
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && KNOWN_CODES.indexOf(code) >= 0
    ? (code as ConversationErrorCode)
    : undefined
}

function parseRetryAfter(headers: unknown): number | undefined {
  if (typeof headers !== 'object' || headers === null) return undefined
  const raw = (headers as Record<string, unknown>)['retry-after']
  const seconds = parseInt(String(raw === undefined ? '' : raw), 10)
  return isFinite(seconds) && seconds >= 0 ? seconds : undefined
}

function parseServerMessage(data: unknown, status: number): string {
  if (typeof data === 'object' && data !== null) {
    const message = (data as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message.trim()
  }
  return `HTTP ${status}`
}

/**
 * Converte erro do axios em erro tipado. O que não for resposta HTTP (timeout,
 * rede, erro do SDK do Twilio) volta intacto — não é do contrato do serviço.
 */
export function toConversationsApiError(err: unknown): unknown {
  if (err instanceof ConversationsApiError) return err
  if (!axios.isAxiosError(err) || !err.response) return err

  const { status, data, headers } = err.response

  return new ConversationsApiError(
    status,
    parseCode(data),
    parseRetryAfter(headers),
    parseServerMessage(data, status)
  )
}

/**
 * Contexto seguro para log e Sentry. Conteúdo de chat é dado clínico: nada de
 * corpo de resposta, corpo de mensagem, nome de anexo ou `identity`.
 */
export function describeConversationsError(err: unknown): {
  status?: number
  code?: string
  retryAfterSeconds?: number
  route?: string
} {
  if (err instanceof ConversationsApiError) {
    return {
      status: err.status,
      code: err.code,
      retryAfterSeconds: err.retryAfterSeconds,
    }
  }
  if (axios.isAxiosError(err)) {
    return {
      status: err.response && err.response.status,
      // `url` é o path que o cliente montou, sem a query de identificação.
      route: err.config && err.config.url && err.config.url.split('?')[0],
    }
  }
  return {}
}

const BACKOFF_MS = [500, 2000, 6000]

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * Retenta **só** o que é transitório. Capacidade (503) e erro permanente sobem
 * na hora: retry indiscriminado em 5xx foi o que saturou o `POST /conversations`.
 */
export async function withConversationsRetry<T>(
  run: () => Promise<T>
): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run()
    } catch (err) {
      const typed = toConversationsApiError(err)
      const shouldRetry =
        typed instanceof ConversationsApiError &&
        typed.isTransient &&
        attempt < BACKOFF_MS.length

      if (!shouldRetry) throw typed

      await sleep(BACKOFF_MS[attempt])
    }
  }
}
